import {
  compareFinancialAidApplicationHistory,
  getFinancialAidAcademicYearStartYear,
} from '@triedge/financial-aid-domain/value-objects';

import {
  normalizeFinancialAidAcademicYearLabel,
  resolveFinancialAidDisplayYears,
} from './financialAidAcademicYear';

export type ParentFinancialAidApplicationLike = Record<string, unknown>;

export type ParentFinancialAidApplicationSummary = {
  id: string;
  code: string;
  academicYear: string;
  statusToken: string;
  statusLabel: string;
  submittedDateLabel: string | null;
  updatedDateLabel: string | null;
  nextStep: string;
  canResume: boolean;
  requestedInfo?: string;
  responseDeadline?: string | number | null;
  moreInfoRequestId?: string;
  moreInfoRequestDiscriminator?: string | number | null;
};

export type ParentFinancialAidExistingApplicationState = {
  kind: 'started' | 'submitted';
  title: string;
  body: string;
  primaryAction: 'continue' | 'view_status';
  primaryLabel: string;
  secondaryLabel: string;
};

const STATUS_LABELS: Record<string, string> = {
  invited: 'Invited',
  opened: 'Opened',
  started: 'In Progress',
  in_progress: 'In Progress',
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  more_info_needed: 'More Info Needed',
  approved: 'Approved',
  denied: 'Denied',
  award_accepted: 'Award Accepted',
  award_declined: 'Award Declined',
};

const RESUMABLE_STATUS_TOKENS = new Set(['invited', 'opened', 'started', 'draft']);
const EXISTING_APPLICATION_RECLICK_STATUS_TOKENS = new Set([
  'draft',
  'started',
  'in_progress',
  'submitted',
  'under_review',
  'more_info_needed',
  'approved',
  'denied',
  'accepted',
  'declined',
  'award_accepted',
  'award_declined',
]);

const readText = (value: unknown): string => String(value ?? '').trim();
const normalizeAcademicYearForComparison = (value: unknown): string =>
  normalizeFinancialAidAcademicYearLabel(value) ?? readText(value);

export const isParentFinancialAidAcademicYearAfter = (
  candidateAcademicYear: unknown,
  referenceAcademicYear: unknown,
): boolean => {
  const candidateStartYear = getFinancialAidAcademicYearStartYear(candidateAcademicYear);
  const referenceStartYear = getFinancialAidAcademicYearStartYear(referenceAcademicYear);
  return candidateStartYear !== null
    && referenceStartYear !== null
    && candidateStartYear > referenceStartYear;
};

export const normalizeParentFinancialAidStatusToken = (value: unknown): string =>
  readText(value)
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const isParentFinancialAidApplicationResumable = (status: unknown): boolean =>
  RESUMABLE_STATUS_TOKENS.has(normalizeParentFinancialAidStatusToken(status));

export const canDownloadParentFinancialAidApplicationCopy = (status: unknown): boolean =>
  Boolean(normalizeParentFinancialAidStatusToken(status))
  && !isParentFinancialAidApplicationResumable(status);

export const isParentFinancialAidExistingApplicationStateStatus = (status: unknown): boolean =>
  EXISTING_APPLICATION_RECLICK_STATUS_TOKENS.has(normalizeParentFinancialAidStatusToken(status));

export const formatParentFinancialAidStatusLabel = (status: unknown): string => {
  const token = normalizeParentFinancialAidStatusToken(status);
  if (STATUS_LABELS[token]) return STATUS_LABELS[token];
  return token
    ? token
      .split('_')
      .filter(Boolean)
      .map((segment) => `${segment.slice(0, 1).toUpperCase()}${segment.slice(1)}`)
      .join(' ')
    : 'Status Pending';
};

const toEpochMilliseconds = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }
  const text = readText(value);
  if (!text) return 0;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDate = (value: unknown): string | null => {
  const timestampMs = toEpochMilliseconds(value);
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) return null;
  return new Date(timestampMs).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const hasSubmittedTimestamp = (application: ParentFinancialAidApplicationLike): boolean =>
  toEpochMilliseconds(application.submittedAt) > 0;

const isParentFinancialAidApplicationDraftResumable = (
  application: ParentFinancialAidApplicationLike,
  statusToken: string,
): boolean =>
  isParentFinancialAidApplicationResumable(statusToken) && !hasSubmittedTimestamp(application);

const getSortTimestamp = (application: ParentFinancialAidApplicationLike): number =>
  toEpochMilliseconds(
    application.submittedAt
    ?? application.lastActivityAt
    ?? application.updatedAt
    ?? application.createdAt,
  );

const APPLICATION_HISTORY_STATUS_PRIORITY: Record<string, number> = {
  award_accepted: 110,
  award_declined: 110,
  approved: 100,
  denied: 100,
  more_info_needed: 95,
  under_review: 90,
  in_progress: 88,
  submitted: 85,
  draft: 50,
  started: 45,
  opened: 40,
  invited: 35,
};

const getApplicationHistoryPriority = (application: ParentFinancialAidApplicationLike): number => {
  const token = normalizeParentFinancialAidStatusToken(application.status);
  return APPLICATION_HISTORY_STATUS_PRIORITY[token] ?? 60;
};

const getLogicalApplicationKey = (application: ParentFinancialAidApplicationLike): string => {
  const id = readText(application.id);
  const applicantId = readText(application.applicantId);
  const academicYear = normalizeFinancialAidAcademicYearLabel(application.academicYear)
    ?? readText(application.academicYear);
  const code = readText(application.code);
  if (academicYear) return `year:${applicantId || 'unknown-applicant'}:${academicYear}`;
  if (code) return `code:${applicantId}:${code}`;
  return `id:${id}`;
};

const preferApplicationForHistory = (
  candidate: ParentFinancialAidApplicationLike,
  existing: ParentFinancialAidApplicationLike,
): ParentFinancialAidApplicationLike => {
  const priorityDelta = getApplicationHistoryPriority(candidate) - getApplicationHistoryPriority(existing);
  if (priorityDelta > 0) return candidate;
  if (priorityDelta < 0) return existing;
  return getSortTimestamp(candidate) >= getSortTimestamp(existing) ? candidate : existing;
};

const dedupeParentFinancialAidApplications = (
  applications: readonly ParentFinancialAidApplicationLike[],
): ParentFinancialAidApplicationLike[] => {
  const byLogicalApplication = new Map<string, ParentFinancialAidApplicationLike>();
  applications.forEach((application) => {
    const key = getLogicalApplicationKey(application);
    const existing = byLogicalApplication.get(key);
    byLogicalApplication.set(
      key,
      existing ? preferApplicationForHistory(application, existing) : application,
    );
  });
  return Array.from(byLogicalApplication.values());
};

const resolveNextStep = (status: unknown, canResume = isParentFinancialAidApplicationResumable(status)): string => {
  const token = normalizeParentFinancialAidStatusToken(status);
  if (canResume) {
    return 'Continue your financial aid application when you are ready.';
  }
  if (token === 'in_progress') {
    return 'The school office is reviewing your application.';
  }
  if (token === 'submitted') {
    return 'Your application has been submitted to the school office.';
  }
  if (token === 'under_review') {
    return 'The school office is reviewing your application.';
  }
  if (token === 'more_info_needed') {
    return 'The school office requested more information before completing review.';
  }
  if (token === 'approved') {
    return 'A financial aid decision is available.';
  }
  if (token === 'denied') {
    return 'A financial aid decision has been recorded.';
  }
  if (token === 'award_accepted') {
    return 'Your financial aid award has been accepted.';
  }
  if (token === 'award_declined') {
    return 'Your financial aid award has been declined.';
  }
  return 'Check this page for updates from the school office.';
};

export const selectCurrentParentFinancialAidApplication = (
  applications: readonly ParentFinancialAidApplicationLike[] | null | undefined,
  input: {
    applicantId?: string | null;
    academicYear?: string | null;
    includeFutureYears?: boolean;
  } = {},
): ParentFinancialAidApplicationLike | null => {
  const applicantId = readText(input.applicantId);
  const academicYear = normalizeAcademicYearForComparison(input.academicYear);
  const candidates = dedupeParentFinancialAidApplications(
    (applications ?? [])
    .filter((application) => {
      if (!readText(application.id)) return false;
      if (applicantId && readText(application.applicantId) !== applicantId) return false;
      return true;
    }),
  )
    .sort((left, right) => getSortTimestamp(right) - getSortTimestamp(left));

  if (!candidates.length) return null;

  if (academicYear) {
    const currentYearApplication = candidates.find(
      (application) => normalizeAcademicYearForComparison(application.academicYear) === academicYear,
    );
    if (currentYearApplication) {
      return currentYearApplication;
    }

    if (input.includeFutureYears === true) {
      const academicYearStart = getFinancialAidAcademicYearStartYear(academicYear);
      const futureYearApplication = academicYearStart === null
        ? null
        : [...candidates]
          .filter((application) => isParentFinancialAidAcademicYearAfter(application.academicYear, academicYear))
          .sort((left, right) => {
            const leftStart = getFinancialAidAcademicYearStartYear(left.academicYear);
            const rightStart = getFinancialAidAcademicYearStartYear(right.academicYear);
            if (leftStart !== null && rightStart !== null && leftStart !== rightStart) {
              return leftStart - rightStart;
            }
            return compareFinancialAidApplicationHistory(left, right);
          })[0] ?? null;
      if (futureYearApplication) return futureYearApplication;
    }

    if (currentYearApplication) return currentYearApplication;
  }

  return candidates[0] ?? null;
};

export const summarizeParentFinancialAidApplication = (
  application: ParentFinancialAidApplicationLike | null | undefined,
): ParentFinancialAidApplicationSummary | null => {
  if (!application) return null;
  const id = readText(application.id);
  if (!id) return null;

  const statusToken = normalizeParentFinancialAidStatusToken(application.status);
  const canResume = isParentFinancialAidApplicationDraftResumable(application, statusToken);
  const decision = application.decision && typeof application.decision === 'object' && !Array.isArray(application.decision)
    ? application.decision as Record<string, unknown>
    : {};
  const moreInfoRequestDiscriminator =
    application.requestedAt
    ?? application.requestedAtMs
    ?? application.statusChangedAt
    ?? application.lastUpdatedAt
    ?? application.updatedAt
    ?? application.lastActivityAt
    ?? decision.decidedAt
    ?? decision.createdAt
    ?? decision.updatedAt
    ?? null;
  return {
    id,
    code: readText(application.code) || id,
    academicYear: resolveFinancialAidDisplayYears(application.academicYear).academicYear || 'Selected financial aid cycle',
    statusToken,
    statusLabel: formatParentFinancialAidStatusLabel(statusToken),
    submittedDateLabel: formatDate(application.submittedAt),
    updatedDateLabel: formatDate(application.updatedAt ?? application.lastActivityAt ?? application.createdAt),
    nextStep: resolveNextStep(statusToken, canResume),
    canResume,
    requestedInfo: readText(application.requestedInfo) || undefined,
    responseDeadline: typeof application.responseDeadline === 'string'
      || typeof application.responseDeadline === 'number'
      || application.responseDeadline === null
      ? application.responseDeadline
      : undefined,
    moreInfoRequestId: readText(application.moreInfoRequestId) || undefined,
    moreInfoRequestDiscriminator: typeof moreInfoRequestDiscriminator === 'string'
      || typeof moreInfoRequestDiscriminator === 'number'
      || moreInfoRequestDiscriminator === null
      ? moreInfoRequestDiscriminator
      : undefined,
  };
};

export const resolveCurrentParentFinancialAidApplicationSummary = (
  applications: readonly ParentFinancialAidApplicationLike[] | null | undefined,
  input: {
    applicantId?: string | null;
    academicYear?: string | null;
    includeFutureYears?: boolean;
  } = {},
): ParentFinancialAidApplicationSummary | null =>
  summarizeParentFinancialAidApplication(
    selectCurrentParentFinancialAidApplication(applications, input),
  );

export const summarizeParentFinancialAidApplications = (
  applications: readonly ParentFinancialAidApplicationLike[] | null | undefined,
  input: {
    applicantId?: string | null;
    academicYear?: string | null;
    includeFutureYears?: boolean;
  } = {},
): ParentFinancialAidApplicationSummary[] => {
  const applicantId = readText(input.applicantId);
  const maxAcademicYearStart = input.includeFutureYears === true
    ? null
    : getFinancialAidAcademicYearStartYear(input.academicYear);
  return dedupeParentFinancialAidApplications(
    (applications ?? [])
    .filter((application) => {
      if (!readText(application.id)) return false;
      if (applicantId && readText(application.applicantId) !== applicantId) return false;
      const applicationYearStart = getFinancialAidAcademicYearStartYear(application.academicYear);
      if (
        maxAcademicYearStart !== null
        && applicationYearStart !== null
        && applicationYearStart > maxAcademicYearStart
      ) {
        return false;
      }
      return true;
    }),
  )
    .sort(compareFinancialAidApplicationHistory)
    .map(summarizeParentFinancialAidApplication)
    .filter((summary): summary is ParentFinancialAidApplicationSummary => Boolean(summary));
};

export const resolveParentFinancialAidExistingApplicationState = (
  summary: ParentFinancialAidApplicationSummary | null | undefined,
  academicYear: string | null | undefined,
): ParentFinancialAidExistingApplicationState | null => {
  const requestedYear = normalizeAcademicYearForComparison(academicYear);
  if (!summary || !requestedYear || summary.academicYear !== requestedYear) {
    return null;
  }

  if (summary.canResume) {
    return {
      kind: 'started',
      title: `You've already started a financial aid application for ${summary.academicYear}`,
      body: 'You can continue where you left off. The school will not receive the application until you complete and submit it.',
      primaryAction: 'continue',
      primaryLabel: 'Continue Application',
      secondaryLabel: 'Return to portal home',
    };
  }

  const title = summary.submittedDateLabel || summary.statusToken === 'submitted'
    ? `You've already submitted a financial aid application for ${summary.academicYear}`
    : `You already have a financial aid application for ${summary.academicYear}`;

  return {
    kind: 'submitted',
    title,
    body: 'Your application is already on file. You can review its current status instead of starting another application.',
    primaryAction: 'view_status',
    primaryLabel: 'View Application Status',
    secondaryLabel: 'Return to portal home',
  };
};
